import { getEffectiveGps } from './deviceDisplayUtils';
import { listCapDevices, normalizeSerial } from './optimizeScopeUtils';

/**
 * Devices that can be shown and picked on the selection map: those with a
 * position. Serials keep the casing of the device list, so selections
 * match what the scope pickers store.
 *
 * @returns {Array<{serial:string, lat:number, lng:number, isCap:boolean, capSerial:string, name:string}>}
 */
export function mapSelectableDevices(devices) {
  const capSet = new Set(listCapDevices(devices || []).map((d) => normalizeSerial(d.serial_number)));
  const out = [];
  (devices || []).forEach((d) => {
    const serial = d?.serial_number;
    if (!serial) return;
    const gps = getEffectiveGps(d);
    if (!gps) return;
    out.push({
      serial,
      lat: gps.lat,
      lng: gps.lng,
      isCap: capSet.has(normalizeSerial(serial)),
      capSerial: normalizeSerial(d.cap_serial),
      name: d.name && d.name !== serial ? d.name : '',
    });
  });
  return out;
}

/**
 * The CAPs a set of devices belongs to: a selected CAP is its own CAP;
 * any other device counts for the CAP of its tree. Returned as the CAPs'
 * serials from the device list, in device-list order. Devices without a
 * known CAP add nothing.
 */
export function capsOfSerials(devices, serials) {
  const caps = listCapDevices(devices || []);
  const capByNorm = new Map(caps.map((c) => [normalizeSerial(c.serial_number), c.serial_number]));
  const devByNorm = new Map((devices || []).filter((d) => d?.serial_number).map((d) => [normalizeSerial(d.serial_number), d]));
  const wanted = new Set();
  (serials || []).forEach((sn) => {
    const norm = normalizeSerial(sn);
    if (capByNorm.has(norm)) {
      wanted.add(norm);
      return;
    }
    const cap = normalizeSerial(devByNorm.get(norm)?.cap_serial);
    if (cap && capByNorm.has(cap)) wanted.add(cap);
  });
  return caps.map((c) => c.serial_number).filter((sn) => wanted.has(normalizeSerial(sn)));
}

/**
 * Iperf target from a map selection: the CAPs of the selected devices are
 * measured; with `limitToSelection` the test is limited to the selected
 * devices that are not CAPs (and their uplink paths), otherwise each CAP's
 * whole tree is measured.
 */
export function iperfTargetFromMapSelection(devices, serials, limitToSelection) {
  const capSerials = capsOfSerials(devices, serials);
  const capNorm = new Set(listCapDevices(devices || []).map((c) => normalizeSerial(c.serial_number)));
  const focusSerials = limitToSelection
    ? (serials || []).filter((sn) => !capNorm.has(normalizeSerial(sn)))
    : [];
  return { allCaps: false, capSerials, focusSerials };
}

/**
 * Serials the iperf map picker starts from: the limited devices when the
 * test is limited, otherwise the selected CAPs (every CAP for "All CAPs").
 */
export function iperfMapInitialSerials(value, devices) {
  if (value?.focusSerials?.length) return value.focusSerials;
  if (value?.allCaps) return listCapDevices(devices || []).map((c) => c.serial_number);
  return value?.capSerials || [];
}
